import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Search, MapPin, Building2, Award, ArrowRight, Users, UserPlus, Check, Clock, Loader2, BadgeCheck, ExternalLink } from "lucide-react";
import { PlatformLayout } from "./PlatformLayout";
import { Reveal } from "./Reveal";
import { AnimatedTitle } from "./AnimatedTitle";
import { PortalBanner } from "./PortalBanner";
import { fetchMembers } from "./api";
import { useAuth, authApi } from "./authApi";

export default function Partners() {
  const { user } = useAuth();
  const isMember = user && typeof user === "object";
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [prof, setProf] = useState("");
  const [cityF, setCityF] = useState("");
  const [relMap, setRelMap] = useState({}); // member_id -> { relationship, partnership_id }
  const [busy, setBusy] = useState("");

  const load = useCallback(() => {
    setLoading(true);
    fetchMembers({}).then((d) => { setMembers(Array.isArray(d) ? d : []); setLoading(false); });
  }, []);

  const loadRelations = useCallback(async () => {
    if (!isMember) { setRelMap({}); return; }
    try {
      const d = await authApi.netDirectory("");
      const map = {};
      (d.members || []).forEach((m) => { map[m.member_id] = { relationship: m.relationship, partnership_id: m.partnership_id }; });
      setRelMap(map);
    } catch (_) { setRelMap({}); }
  }, [isMember]);

  useEffect(() => { load(); loadRelations(); }, [load, loadRelations]);

  const professions = React.useMemo(() => Array.from(new Set(members.map((m) => (m.account_type || "").trim()).filter(Boolean))).sort((a, b) => a.localeCompare(b, "fr")), [members]);
  const cities = React.useMemo(() => Array.from(new Set(members.map((m) => (m.city || "").trim()).filter(Boolean))).sort((a, b) => a.localeCompare(b, "fr")), [members]);
  const filtered = React.useMemo(() => {
    const needle = q.trim().toLowerCase();
    return members.filter((m) => {
      if (prof && (m.account_type || "").trim() !== prof) return false;
      if (cityF && (m.city || "").trim() !== cityF) return false;
      if (needle) {
        const hay = `${m.display_name || ""} ${m.company || ""} ${m.city || ""} ${m.account_type || ""}`.toLowerCase();
        if (!hay.includes(needle)) return false;
      }
      return true;
    });
  }, [members, prof, cityF, q]);
  const resetFilters = () => { setQ(""); setProf(""); setCityF(""); };

  const doRequest = async (mid) => {
    setBusy(mid);
    try { await authApi.netRequest(mid); setRelMap((r) => ({ ...r, [mid]: { relationship: "pending_out", partnership_id: null } })); } catch (_) {}
    setBusy("");
  };
  const doAccept = async (mid, pid) => {
    if (!pid) return;
    setBusy(mid);
    try { await authApi.netAccept(pid); setRelMap((r) => ({ ...r, [mid]: { relationship: "accepted", partnership_id: pid } })); } catch (_) {}
    setBusy("");
  };

  const NetAction = ({ m }) => {
    if (!isMember) return null;
    const rel = relMap[m.member_id];
    if (!rel) return null; // self ou non répertorié
    const loadingBtn = busy === m.member_id;
    if (rel.relationship === "accepted")
      return <span className="inline-flex items-center gap-1.5 text-xs font-medium uppercase tracking-wider text-[#C19A6B]" data-testid={`net-rel-accepted-${m.member_id}`}><Check className="w-3.5 h-3.5" /> Dans mon réseau</span>;
    if (rel.relationship === "pending_out")
      return <span className="inline-flex items-center gap-1.5 text-xs font-medium uppercase tracking-wider text-zinc-500" data-testid={`net-rel-pending-${m.member_id}`}><Clock className="w-3.5 h-3.5" /> Demande envoyée</span>;
    if (rel.relationship === "pending_in")
      return (
        <button onClick={() => doAccept(m.member_id, rel.partnership_id)} disabled={loadingBtn} data-testid={`net-accept-${m.member_id}`}
          className="inline-flex items-center gap-1.5 text-xs font-medium uppercase tracking-wider bg-[#C19A6B] hover:bg-[#d1af84] text-white px-4 py-2 rounded-[5px] transition-colors disabled:opacity-60">
          {loadingBtn ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />} Accepter dans mon réseau
        </button>
      );
    return (
      <button onClick={() => doRequest(m.member_id)} disabled={loadingBtn} data-testid={`net-add-${m.member_id}`}
        className="inline-flex items-center gap-1.5 text-xs font-medium uppercase tracking-wider border border-[#C19A6B]/50 text-[#C19A6B] hover:bg-[#C19A6B]/10 px-4 py-2 rounded-[5px] transition-colors disabled:opacity-60">
        {loadingBtn ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <UserPlus className="w-3.5 h-3.5" />} Ajouter à mon réseau
      </button>
    );
  };

  return (
    <PlatformLayout searchDock={false}>
      <PortalBanner
        image="https://static.prod-images.emergentagent.com/jobs/8e57cf35-f4fb-40ce-b502-ff37f5224c96/images/58c361441054242fc19bac63dd12f3c634352af74b086e5fcaf94171313a6b20.jpeg"
        imagePos="center 43%"
        subtitle="Nos conseillers & agents immobiliers, sélectionnés pour leur exigence de discrétion et d'excellence"
        title="Notre réseau"
        testid="partners-hero"
      />
      <div className="bg-zinc-100">
      <div className="pt-0 pb-24 max-w-[1400px] mx-auto px-6 lg:px-12" data-testid="partners-page">
        <div className="max-w-3xl mx-auto -mt-[30px] sm:-mt-[34px] mb-6 relative z-40 bg-[#0d0d10] backdrop-blur-xl border border-white/10 rounded-[5px] p-2.5 sm:p-3 shadow-2xl">
          <div className="flex flex-col sm:flex-row gap-2">
            <div className="relative flex-1">
              <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-[#C19A6B]" />
              <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Nom, société, ville…" data-testid="partners-search" className="w-full bg-white/[0.06] border border-white/15 rounded-full pl-10 pr-4 py-2.5 text-sm text-white placeholder-zinc-400 focus:outline-none focus:border-[#C19A6B] transition-colors" />
            </div>
            <select value={prof} onChange={(e) => setProf(e.target.value)} data-testid="partners-filter-profession" className="sm:w-52 bg-white/[0.06] border border-white/15 rounded-full px-4 py-2.5 text-sm text-white focus:outline-none focus:border-[#C19A6B] transition-colors">
              <option value="" className="bg-[#0d0d10]">Toutes les professions</option>
              {professions.map((p) => <option key={p} value={p} className="bg-[#0d0d10]">{p}</option>)}
            </select>
            <select value={cityF} onChange={(e) => setCityF(e.target.value)} data-testid="partners-filter-city" className="sm:w-44 bg-white/[0.06] border border-white/15 rounded-full px-4 py-2.5 text-sm text-white focus:outline-none focus:border-[#C19A6B] transition-colors">
              <option value="" className="bg-[#0d0d10]">Toutes les villes</option>
              {cities.map((c) => <option key={c} value={c} className="bg-[#0d0d10]">{c}</option>)}
            </select>
          </div>
        </div>

        <div className="flex flex-wrap items-center justify-center gap-3 mb-10">
          <span className="inline-flex flex-col items-center gap-0.5" data-testid="partners-count"><span className="calli text-3xl sm:text-4xl text-[#C19A6B] leading-none">{filtered.length} professionnel{filtered.length > 1 ? "s" : ""}</span>{(q || prof || cityF) && <button onClick={resetFilters} data-testid="partners-reset" className="text-xs text-[#C19A6B] hover:underline">réinitialiser les filtres</button>}</span>
          {!isMember && (
            <Link to="/rejoindre" data-testid="partners-join-btn" className="inline-flex items-center gap-2 bg-[#C19A6B] hover:bg-[#d1af84] text-white text-xs font-semibold uppercase tracking-wider px-5 py-2.5 rounded-full shadow-md shadow-[#C19A6B]/20 transition-colors">
              <UserPlus className="w-4 h-4" /> Nous rejoindre
            </Link>
          )}
        </div>

        {isMember && (
          <p className="text-center text-xs text-zinc-500 mb-8 -mt-4" data-testid="partners-member-hint">
            Vous êtes connecté : ajoutez directement des conseillers à votre réseau depuis les cartes ci-dessous.
          </p>
        )}

        {loading ? (
          <p className="text-center text-zinc-400 py-16">Chargement…</p>
        ) : filtered.length === 0 ? (
          <div className="text-center py-20 text-zinc-400" data-testid="partners-empty"><Users className="w-8 h-8 mx-auto mb-3 text-zinc-300" />Aucun conseiller ne correspond à votre recherche.</div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="partners-grid">
            {filtered.map((m, i) => (
              <motion.div key={m.member_id} initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-40px" }} transition={{ duration: 0.5, delay: (i % 3) * 0.08 }} whileHover={{ y: -4 }}
                className="h-full bg-white border border-zinc-200 rounded-[14px] p-4 hover:border-[#C19A6B]/50 hover:shadow-lg hover:shadow-black/5 transition-all flex flex-col" data-testid={`partner-card-${m.slug}`}>
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-full overflow-hidden border border-[#C19A6B]/40 bg-zinc-100 flex items-center justify-center shrink-0">
                    {m.photo ? <img src={m.photo} alt={m.display_name} className="w-full h-full object-cover" /> : <Building2 className="w-6 h-6 text-[#C19A6B]/50" />}
                  </div>
                  <div className="min-w-0">
                    <h3 className="text-base font-light text-zinc-900 truncate flex items-center gap-1.5">
                      <span className="truncate">{m.display_name}</span>
                      {(m.verified || m.is_founder) && <BadgeCheck className="w-4 h-4 shrink-0 text-[#C19A6B]" data-testid={`partner-verified-${m.slug}`} />}
                    </h3>
                    {m.company && <p className="text-[11px] text-[#C19A6B] uppercase tracking-wider truncate">{m.company}</p>}
                  </div>
                </div>
                <div className="mt-3 flex items-center justify-between gap-3">
                  <div className="space-y-1 text-[13px] text-zinc-600 min-w-0">
                    {m.account_type && <p className="flex items-center gap-1.5 truncate"><Award className="w-3.5 h-3.5 text-[#C19A6B] shrink-0" /> <span className="truncate">{m.account_type}</span></p>}
                    {m.city && <p className="flex items-center gap-1.5 truncate"><MapPin className="w-3.5 h-3.5 text-[#C19A6B] shrink-0" /> <span className="truncate">{m.city}</span></p>}
                  </div>
                  <div className="flex flex-col items-end gap-1.5 shrink-0">
                    <Link to={`/${m.slug}`} className="group inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider text-[#C19A6B] hover:gap-1.5 transition-all">Voir le profil <ArrowRight className="w-3 h-3" /></Link>
                    {m.site_online && <a href={`/${m.slug}/site`} target="_blank" rel="noopener noreferrer" data-testid={`partner-site-${m.slug}`} className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider text-white bg-[#C19A6B] hover:bg-[#A88358] px-2.5 py-1 rounded-md transition-colors"><ExternalLink className="w-3 h-3" /> Voir son site</a>}
                  </div>
                </div>
                {isMember && relMap[m.member_id] && (
                  <div className="mt-3 pt-3 border-t border-zinc-200"><NetAction m={m} /></div>
                )}
              </motion.div>
            ))}
          </div>
        )}
      </div>
      </div>
    </PlatformLayout>
  );
}
